import type { ColumnDef } from '@tanstack/react-table';
import { generatePath } from 'react-router-dom';
import routes from '~/routes';
import { EntityLink } from 'components/entity-link';
import { PlainLink } from 'components/link';
import { EMPTY_CELL } from 'utils/empty-cell';
import { ReversalCell } from './reversal-cell';
import { matchResolutionLabel } from './match-resolution';
import { OutcomeTag } from './outcome-tag';
import { ReasonCell } from './reason-cell';
import type { AutoMatchDecisionRow, AutoMatchOutcome } from './types';
import { DATETIME_COLUMN_WIDTH } from '../utils';
import shared from '../admin-log.module.scss';

export const DECISION_COLUMNS: ColumnDef<AutoMatchDecisionRow>[] = [
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: 'When',
    size: DATETIME_COLUMN_WIDTH,
    meta: { type: 'datetime' },
  },
  {
    id: 'outcome',
    accessorKey: 'outcome',
    header: 'Outcome',
    size: 130,
    cell: (info) => (
      <OutcomeTag outcome={info.getValue() as AutoMatchOutcome} />
    ),
  },
  {
    id: 'reason',
    header: 'Basis / reason',
    size: 150,
    // accessorFn so the header is sortable; the server orders on the same
    // value (COALESCE(basis, suppression_reason)).
    accessorFn: (row) =>
      row.outcome === 'auto_confirmed' ? row.basis : row.suppressionReason,
    cell: (info) => <ReasonCell row={info.row.original} />,
  },
  {
    id: 'c1',
    accessorKey: 'c1',
    header: 'C1',
    size: 70,
    meta: { type: 'integer' },
    cell: (info) => (info.getValue() as number | null) ?? EMPTY_CELL,
  },
  {
    id: 'c2',
    accessorKey: 'c2',
    header: 'C2',
    size: 70,
    meta: { type: 'integer' },
    cell: (info) => (info.getValue() as number | null) ?? EMPTY_CELL,
  },
  {
    id: 'entityName',
    accessorKey: 'entityName',
    header: 'Staged entity',
    size: 240,
    // Name lives on the polymorphic concrete entity, not sortable in one clause.
    enableSorting: false,
    cell: (info) => (
      <EntityLink
        id={info.row.original.entityId}
        name={info.getValue() as string | null}
      />
    ),
  },
  {
    id: 'matchedEntityName',
    accessorKey: 'matchedEntityName',
    header: 'Matched to',
    size: 240,
    enableSorting: false,
    cell: (info) => (
      <EntityLink
        id={info.row.original.matchedEntityId}
        name={info.getValue() as string | null}
        fallback={<span className={shared.muted}>{EMPTY_CELL}</span>}
      />
    ),
  },
  {
    id: 'match',
    header: 'Name match / action',
    size: 180,
    // Half of it derives from a joined reconciliation; not sorted server-side.
    enableSorting: false,
    accessorFn: (row) => matchResolutionLabel(row),
    cell: (info) =>
      (info.getValue() as string | null) ?? (
        <span className={shared.muted}>{EMPTY_CELL}</span>
      ),
  },
  {
    id: 'undone',
    accessorKey: 'undone',
    header: 'Reversal',
    size: 240,
    // Reversal state derives from a joined reconciliation; not sorted server-side.
    enableSorting: false,
    cell: (info) => <ReversalCell row={info.row.original} />,
  },
  {
    id: 'county',
    accessorKey: 'county',
    header: 'County',
    size: 140,
    cell: (info) => (info.getValue() as string | null) || EMPTY_CELL,
  },
  {
    id: 'documentId',
    accessorKey: 'documentId',
    header: 'Document',
    size: 110,
    cell: (info) => {
      const docId = info.getValue() as number | null;
      if (!docId) return <span className={shared.muted}>{EMPTY_CELL}</span>;
      return (
        <PlainLink
          href={generatePath(routes.documentPath, { id: docId })}
          openInNewTab
        >
          #{docId} ↗
        </PlainLink>
      );
    },
  },
];
